import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getRoleById,
  createRole,
  updateRole,
  updateRolePermissions,
  getAvailableModules,
  getPermissionLevels,
  getBetaUsers,
  addBetaUser,
  removeBetaUser,
  searchBetaUsers
} from '../../../api/roles';
import { locationApi } from '../../../api/adminapi';
import { getRoleFromToken } from '../../../utils/jwtUtils';
import './RoleBuilder.css';

function RoleBuilder() {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const isEditMode = !!roleId;

  // Role basic info
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [assignedLocations, setAssignedLocations] = useState('');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [active, setActive] = useState(true);
  const [isSystemRole, setIsSystemRole] = useState(false);

  // Store original values for system roles (to preserve null vs empty string)
  const [originalRoleData, setOriginalRoleData] = useState(null);

  // Location Master dropdown
  const [availableLocations, setAvailableLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  // Permission management
  const [modules, setModules] = useState([]);
  const [permissionLevels, setPermissionLevels] = useState([]);
  const [permissions, setPermissions] = useState({});

  // Beta allowlist state (keyed by module name)
  const [betaUsers, setBetaUsers] = useState({});          // { MODULE_NAME: [{id, userId, userName, userEmail, accessLevel, grantedBy}] }
  const [betaSearchQuery, setBetaSearchQuery] = useState({}); // { MODULE_NAME: query string }
  const [betaSearchResults, setBetaSearchResults] = useState({}); // { MODULE_NAME: [{id, name, email}] }
  const [betaLoading, setBetaLoading] = useState({});       // { MODULE_NAME: bool }
  const [betaAccessLevel, setBetaAccessLevel] = useState({}); // { MODULE_NAME: 'READ'|'WRITE'|'DELETE'|'ADMIN' }

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [userRole, setUserRole] = useState('');

  // Check if current user is SuperAdmin
  useEffect(() => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
      const claims = getRoleFromToken(token);
      setUserRole(claims?.role?.toUpperCase() || '');
    }
  }, []);

  const isSuperAdmin = userRole === 'SUPER_ADMIN';

  useEffect(() => {
    loadMetadata();
    loadLocations();
    if (isEditMode) {
      loadRole();
    }
  }, [roleId]);

  const loadMetadata = async () => {
    try {
      const [modulesData, levelsData] = await Promise.all([
        getAvailableModules(),
        getPermissionLevels()
      ]);
      setModules(modulesData);
      setPermissionLevels(levelsData);
    } catch (err) {
      setError('Failed to load metadata: ' + err.message);
    }
  };

  const loadLocations = async () => {
    try {
      setIsLoadingLocations(true);
      const response = await locationApi.getAllActiveLocationNames();
      const locationNames = response?.data || response || [];
      setAvailableLocations(locationNames);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
      setAvailableLocations([]);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const loadRole = async () => {
    try {
      setLoading(true);
      const roleData = await getRoleById(roleId);

      // Store original data for system roles (to preserve exact values like null)
      if (roleData.isSystemRole) {
        setOriginalRoleData({
          roleName: roleData.roleName,
          description: roleData.description,
          assignedLocations: roleData.assignedLocations,
          active: roleData.active
        });
      }

      setRoleName(roleData.roleName || '');
      setDescription(roleData.description || '');
      setIsSystemRole(roleData.isSystemRole || false);

      // Parse assignedLocations from comma-separated string to array
      const assignedLocsStr = roleData.assignedLocations || '';
      setAssignedLocations(assignedLocsStr);

      if (assignedLocsStr.trim()) {
        const locsArray = assignedLocsStr.split(',').map(loc => loc.trim()).filter(Boolean);
        setSelectedLocations(locsArray);
      } else {
        setSelectedLocations([]);
      }

      setActive(roleData.active);

      // Permissions are already included in roleData from backend
      // Convert permissions object to the format we need
      const permObj = roleData.permissions || {};
      setPermissions(permObj);

      // Pre-load beta allowlists for any modules set to BETA
      const betaModules = Object.entries(permObj)
        .filter(([, level]) => level === 'BETA')
        .map(([moduleName]) => moduleName);
      if (betaModules.length > 0) {
        const betaData = {};
        await Promise.all(betaModules.map(async (moduleName) => {
          try {
            betaData[moduleName] = await getBetaUsers(roleId, moduleName);
          } catch (e) {
            betaData[moduleName] = [];
          }
        }));
        setBetaUsers(betaData);
      }

      // For system roles, automatically switch to permissions tab
      if (roleData.isSystemRole) {
        setActiveTab('permissions');
      }

      setError(null);
    } catch (err) {
      setError('Failed to load role: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (moduleName, level) => {
    setPermissions(prev => ({
      ...prev,
      [moduleName]: level
    }));
  };

  const handleRemovePermission = (moduleName) => {
    setPermissions(prev => {
      const newPerms = { ...prev };
      delete newPerms[moduleName];
      return newPerms;
    });
  };

  // ===== BETA ALLOWLIST HANDLERS =====

  const loadBetaUsers = async (moduleName) => {
    if (!roleId) return;
    setBetaLoading(prev => ({ ...prev, [moduleName]: true }));
    try {
      const users = await getBetaUsers(roleId, moduleName);
      setBetaUsers(prev => ({ ...prev, [moduleName]: users }));
    } catch (err) {
      console.error('Failed to load beta users for', moduleName, err);
    } finally {
      setBetaLoading(prev => ({ ...prev, [moduleName]: false }));
    }
  };

  const handlePermissionChangeWithBeta = (moduleName, level) => {
    const prevLevel = permissions[moduleName];
    handlePermissionChange(moduleName, level);
    // When switching TO beta, load existing allowlist
    if (level === 'BETA' && prevLevel !== 'BETA' && roleId) {
      loadBetaUsers(moduleName);
    }
    // When switching AWAY from beta, clear the local beta state for this module
    if (prevLevel === 'BETA' && level !== 'BETA') {
      setBetaUsers(prev => { const n = { ...prev }; delete n[moduleName]; return n; });
      setBetaSearchQuery(prev => { const n = { ...prev }; delete n[moduleName]; return n; });
      setBetaSearchResults(prev => { const n = { ...prev }; delete n[moduleName]; return n; });
      setBetaAccessLevel(prev => { const n = { ...prev }; delete n[moduleName]; return n; });
    }
  };

  const handleBetaSearch = async (moduleName, query) => {
    setBetaSearchQuery(prev => ({ ...prev, [moduleName]: query }));
    if (!query || query.length < 2) {
      setBetaSearchResults(prev => ({ ...prev, [moduleName]: [] }));
      return;
    }
    try {
      const results = await searchBetaUsers(query, roleId || undefined);
      // Filter out users already on the allowlist
      const existing = betaUsers[moduleName] || [];
      const existingIds = new Set(existing.map(u => u.userId));
      setBetaSearchResults(prev => ({
        ...prev,
        [moduleName]: results.filter(u => !existingIds.has(u.id))
      }));
    } catch (err) {
      console.error('Beta user search failed:', err);
    }
  };

  const handleAddBetaUser = async (moduleName, userId) => {
    if (!roleId) return;
    const accessLevel = betaAccessLevel[moduleName] || 'READ';
    try {
      const result = await addBetaUser(roleId, moduleName, userId, accessLevel);
      if (result.success) {
        await loadBetaUsers(moduleName);
        // Clear search
        setBetaSearchQuery(prev => ({ ...prev, [moduleName]: '' }));
        setBetaSearchResults(prev => ({ ...prev, [moduleName]: [] }));
      } else {
        alert(result.message || 'Failed to add user to beta allowlist');
      }
    } catch (err) {
      alert('Failed to add user: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleRemoveBetaUser = async (moduleName, overrideId) => {
    try {
      await removeBetaUser(overrideId);
      setBetaUsers(prev => ({
        ...prev,
        [moduleName]: (prev[moduleName] || []).filter(u => u.id !== overrideId)
      }));
    } catch (err) {
      alert('Failed to remove user: ' + err.message);
    }
  };

  const handleLocationToggle = (locationName) => {
    setSelectedLocations(prev => {
      if (prev.includes(locationName)) {
        return prev.filter(loc => loc !== locationName);
      } else {
        return [...prev, locationName];
      }
    });
  };

  const handleSelectAllLocations = () => {
    if (selectedLocations.length === availableLocations.length) {
      setSelectedLocations([]);
    } else {
      setSelectedLocations([...availableLocations]);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Validate - for custom roles only
      if (!isSystemRole && !roleName.trim()) {
        alert('Role name is required');
        return;
      }

      // Convert permissions object to array
      const permissionsArray = Object.entries(permissions).map(([module, level]) => ({
        module,
        level
      }));

      if (isEditMode) {
        // Convert selectedLocations array to comma-separated string for backend
        const assignedLocsString = selectedLocations.join(', ');

        const roleData = {
          roleName: roleName.trim(),
          description: description.trim(),
          assignedLocations: assignedLocsString,
          active,
          permissions: permissions
        };

        await updateRole(roleId, roleData);
        if (!isSystemRole || !isSuperAdmin) {
          await updateRolePermissions(roleId, permissionsArray);
        }
      } else {
        // Convert selectedLocations array to comma-separated string for backend
        const assignedLocsString = selectedLocations.join(', ');

        const roleData = {
          roleName: roleName.trim(),
          description: description.trim(),
          assignedLocations: assignedLocsString,
          active
        };

        const newRole = await createRole(roleData);
        if (permissionsArray.length > 0) {
          await updateRolePermissions(newRole.id, permissionsArray);
        }
      }

      navigate('/admin/roles');
    } catch (err) {
      setError('Failed to save role: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/roles');
  };

  if (loading && isEditMode) {
    return <div className="role-builder-loading">Loading role...</div>;
  }

  return (
    <div className="role-builder-container">
      <div className="role-builder-header">
        <div>
          <button className="btn-back" onClick={handleCancel}>
            ← Back to Roles
          </button>
          <h1>
            {isEditMode ? (isSystemRole ? 'Edit System Role Permissions' : 'Edit Role') : 'Create New Role'}
            {isSystemRole && <span className="system-role-badge">System Role</span>}
          </h1>
          <p>
            {isSystemRole && !isSuperAdmin
              ? 'System roles cannot have their name, description, or locations modified. You can only update permissions.'
              : (isEditMode ? 'Modify role details and permissions' : 'Define a new role with permissions')
            }
          </p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={handleCancel} disabled={loading}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : (isEditMode ? 'Update Role' : 'Create Role')}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="role-builder-tabs">
        {(!isSystemRole || isSuperAdmin) && (
          <button
            className={`tab ${activeTab === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveTab('basic')}
          >
            Basic Information
          </button>
        )}
        <button
          className={`tab ${activeTab === 'permissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('permissions')}
        >
          Permissions ({Object.keys(permissions).length})
        </button>
      </div>

      {activeTab === 'basic' && (!isSystemRole || isSuperAdmin) && (
        <div className="tab-content">
          <div className="form-section">
            <h3>Role Details</h3>
            <div className="form-group">
              <label htmlFor="roleName">Role Name *</label>
              <input
                type="text"
                id="roleName"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="e.g., Facility Manager, Regional Admin"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the role's responsibilities and scope"
                rows="4"
              />
            </div>

            <div className="form-group">
              <label htmlFor="assignedLocations">Assigned Locations</label>
              {isLoadingLocations ? (
                <p>Loading locations...</p>
              ) : (
                <div className="location-selector">
                  <div className="location-selector-header">
                    <button
                      type="button"
                      className="btn-select-all"
                      onClick={handleSelectAllLocations}
                    >
                      {selectedLocations.length === availableLocations.length ? 'Deselect All' : 'Select All'}
                    </button>
                    <span className="selected-count">
                      {selectedLocations.length} of {availableLocations.length} selected
                    </span>
                  </div>
                  <div className="location-checkboxes">
                    {availableLocations.length > 0 ? (
                      availableLocations.map(locationName => (
                        <label key={locationName} className="location-checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedLocations.includes(locationName)}
                            onChange={() => handleLocationToggle(locationName)}
                          />
                          <span>{locationName}</span>
                        </label>
                      ))
                    ) : (
                      <p className="no-locations">No active locations found. Please add locations in Location Master.</p>
                    )}
                  </div>
                </div>
              )}
              <small className="helper-text">
                Leave empty for all locations, or select specific locations
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                Active (users can be assigned to this role)
              </label>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'permissions' && (
        <div className="tab-content">
          <div className="permissions-section">
            <div className="permissions-header">
              <h3>Module Permissions</h3>
              <p>Assign permission levels for each module</p>
            </div>

            <div className="permissions-matrix">
              <div className="matrix-header">
                <div className="header-cell module-header">Module</div>
                <div className="header-cell level-header">Permission Level</div>
                <div className="header-cell action-header">Action</div>
              </div>

              {modules.map(module => {
                // SYSTEM_LOGIN is a simple toggle - just Enabled or Disabled
                const isToggleModule = module.name === 'SYSTEM_LOGIN';
                const isBeta = permissions[module.name] === 'BETA';
                const moduleBetaUsers = betaUsers[module.name] || [];
                const moduleSearchQuery = betaSearchQuery[module.name] || '';
                const moduleSearchResults = betaSearchResults[module.name] || [];

                return (
                  <React.Fragment key={module.name}>
                    <div className="matrix-row">
                      <div className="module-cell">
                        <div className="module-name">{module.displayName}</div>
                        <div className="module-description">{module.description}</div>
                      </div>
                      <div className="level-cell">
                        {isToggleModule ? (
                          // Simple toggle for SYSTEM_LOGIN
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={!!permissions[module.name]}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handlePermissionChange(module.name, 'READ');
                                } else {
                                  handleRemovePermission(module.name);
                                }
                              }}
                            />
                            <span className="toggle-slider"></span>
                            <span className="toggle-label">
                              {permissions[module.name] ? 'Enabled' : 'Disabled'}
                            </span>
                          </label>
                        ) : (
                          // Full permission dropdown for other modules
                          <select
                            value={permissions[module.name] || ''}
                            onChange={(e) => handlePermissionChangeWithBeta(module.name, e.target.value)}
                            className={permissions[module.name] ? 'has-permission' : ''}
                          >
                            <option value="">No Access</option>
                            {permissionLevels.map(level => (
                              <option key={level.name} value={level.name}>
                                {level.displayName} - {level.description}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div className="action-cell">
                        {permissions[module.name] && !isToggleModule && (
                          <button
                            className="btn-remove"
                            onClick={() => handleRemovePermission(module.name)}
                            title="Remove permission"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Beta Allowlist Panel — only shown when module is set to BETA */}
                    {isBeta && (
                      <div className="beta-allowlist-panel">
                        <div className="beta-panel-header">
                          <span className="beta-badge">BETA</span>
                          <span className="beta-panel-title">
                            Beta Allowlist — only these users can access <strong>{module.displayName}</strong>
                          </span>
                          <span className="beta-user-count">{moduleBetaUsers.length} user{moduleBetaUsers.length !== 1 ? 's' : ''}</span>
                        </div>

                        {!isEditMode && (
                          <div className="beta-save-notice">
                            Save the role first, then manage the beta allowlist.
                          </div>
                        )}

                        {isEditMode && (
                          <>
                            {/* Access level + User search row */}
                            <div className="beta-add-row">
                              <select
                                className="beta-level-select"
                                value={betaAccessLevel[module.name] || 'READ'}
                                onChange={(e) => setBetaAccessLevel(prev => ({ ...prev, [module.name]: e.target.value }))}
                                title="Access level granted to added users"
                              >
                                <option value="READ">Read Only</option>
                                <option value="WRITE">Read &amp; Write</option>
                                <option value="DELETE">Full Access (Delete)</option>
                                <option value="ADMIN">Admin</option>
                              </select>
                              <div className="beta-search-wrapper">
                                <input
                                  type="text"
                                  className="beta-search-input"
                                  placeholder="Search users by name or email to add..."
                                  value={moduleSearchQuery}
                                  onChange={(e) => handleBetaSearch(module.name, e.target.value)}
                                />
                                {moduleSearchResults.length > 0 && (
                                  <div className="beta-search-dropdown">
                                    {moduleSearchResults.map(user => (
                                      <div
                                        key={user.id}
                                        className="beta-search-result"
                                        onClick={() => handleAddBetaUser(module.name, user.id)}
                                      >
                                        <span className="beta-result-name">{user.name}</span>
                                        <span className="beta-result-email">{user.email}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {moduleSearchQuery.length >= 2 && moduleSearchResults.length === 0 && (
                                  <div className="beta-no-results">No users found</div>
                                )}
                              </div>
                            </div>

                            {/* Allowlist */}
                            {betaLoading[module.name] ? (
                              <div className="beta-loading">Loading allowlist...</div>
                            ) : moduleBetaUsers.length === 0 ? (
                              <div className="beta-empty">No users on allowlist yet. Search above to add users.</div>
                            ) : (
                              <div className="beta-user-list">
                                {moduleBetaUsers.map(entry => (
                                  <div key={entry.id} className="beta-user-item">
                                    <div className="beta-user-info">
                                      <span className="beta-user-name">{entry.userName}</span>
                                      <span className="beta-user-email">{entry.userEmail}</span>
                                    </div>
                                    <div className="beta-user-meta">
                                      <span className={`beta-access-badge beta-access-${(entry.accessLevel || 'READ').toLowerCase()}`}>
                                        {entry.accessLevel === 'WRITE' ? 'Read & Write'
                                          : entry.accessLevel === 'DELETE' ? 'Full Access'
                                          : entry.accessLevel === 'ADMIN' ? 'Admin'
                                          : 'Read Only'}
                                      </span>
                                      <span className="beta-granted-by">by {entry.grantedBy}</span>
                                      <button
                                        className="beta-remove-btn"
                                        onClick={() => handleRemoveBetaUser(module.name, entry.id)}
                                        title="Remove from beta allowlist"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {Object.keys(permissions).length === 0 && (
              <div className="no-permissions">
                <p>No permissions assigned yet. Select permission levels from the dropdowns above.</p>
              </div>
            )}

            <div className="permissions-summary">
              <h4>Permission Summary</h4>
              <div className="summary-grid">
                {Object.entries(permissions).map(([moduleName, level]) => {
                  const module = modules.find(m => m.name === moduleName);
                  const levelObj = permissionLevels.find(l => l.name === level);
                  return (
                    <div key={moduleName} className="summary-item">
                      <span className="summary-module">{module?.displayName}</span>
                      <span className={`summary-level level-${level?.toLowerCase()}`}>
                        {levelObj?.displayName}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoleBuilder;
